import { Download } from '@mui/icons-material'
import {
  Box,
  Button,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { FileUpload } from 'components/FileUpload'
import { useMemo, useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { procurementSubmissionApi } from 'services/submissions/procurement-submission'
import axiosInstance from 'store/axiosInstance'
import _round from 'lodash/round'
import {
  IProcurementSubmissionItems,
  IProcurementSubmissions,
} from 'types/submissions'
import { downloadExcel } from 'utils/download-file'
import { calculatePriceWithTax } from 'utils/price-with-tax'

const StyledTable = styled(Table)(() => ({
  borderCollapse: 'collapse',
  width: '100%',
  tableLayout: 'fixed',

  '& th, & td': {
    border: '1px solid #dddddd !important',
  },
  '& th': {
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
}))

const StyledTableCell = styled(TableCell)(() => ({
  padding: 0,
  width: '100%',

  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    borderRadius: 0,
  },

  '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-disabled': {
    background: 'rgba(128,128,128, 0.1)',
  },
}))

type Props = {
  procurementSubmissionId: string
  procurementSubmissionItems: IProcurementSubmissionItems
  rowsPerPage: number
  isVatIncluded: boolean
  hasDocDeadlinePassed: boolean
  procurementSubmission: IProcurementSubmissions
}

export const ProcurementSubmissionWorkrateTable = ({
  procurementSubmissionId,
  procurementSubmissionItems,
  rowsPerPage,
  isVatIncluded,
  hasDocDeadlinePassed,
  procurementSubmission,
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { control, watch } = useFormContext()
  const { fields } = useFieldArray({
    control,
    name: `procurementRequirement.procurementRequirementItems`,
  })

  const [priceValue, setPriceValue] = useState('')

  const tableColumnSpan = isVatIncluded ? 8 : 7

  const handleDownloadTemplate = async () => {
    downloadExcel({
      fileName: 'satinalma_predmeti_cedvel',
      url: `/procurement-submissions/${procurementSubmissionId}/items/download-template`,
    })
  }

  const handleUploadTemplate = async (files: any) => {
    try {
      const response = await axiosInstance.post(
        `/procurement-submissions/${procurementSubmissionId}/items/upload-template`,
        files,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      dispatch(
        procurementSubmissionApi.util.invalidateTags(['ProcurementSubmissionsItems'])
      )
    } catch (error) {
      console.error('Error uploading file:', error)
    }
  }

  const allValues = watch('procurementRequirement.procurementRequirementItems')

  const modifiedProcurementSubmissionItems = useMemo(() => {
    return procurementSubmissionItems.items.map((item: any, index: number) => {
      const startRowNumber =
        procurementSubmissionItems?.info?.currentPage * rowsPerPage + index + 1
      const calculatedTotalPrice =
        Number((allValues[index] as any).quantity) *
        Number((allValues[index] as any).price)

      const calculatedVatPrice = calculatePriceWithTax(
        0.18,
        Number(calculatedTotalPrice)
      )

      return { ...item, startRowNumber, calculatedTotalPrice, calculatedVatPrice }
    })
  }, [procurementSubmissionItems, priceValue])

  return (
    <Stack spacing={2}>
      <Typography variant="h6" fontWeight="bolder">
        {t('work-rate-sheet')}
      </Typography>
      <Box
        display="flex"
        justifyContent="flex-end"
        marginBottom={1}
        marginTop={1}
        gap={2}
      >
        <FileUpload
          text={t('add-sheet')}
          handleAsRawFile={(file) => handleUploadTemplate(file)}
          sendToApi={false}
          isMulti
          isDisabled={hasDocDeadlinePassed}
        />
        <Button
          onClick={handleDownloadTemplate}
          variant="text"
          startIcon={<Download />}
          disabled={hasDocDeadlinePassed}
        >
          {t('download-excel-template')}
        </Button>
      </Box>
      <TableContainer>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t('name')}</TableCell>
              <TableCell>{t('description')}</TableCell>
              <TableCell>{t('quantity')}</TableCell>
              <TableCell>{t('unit')}</TableCell>
              <TableCell>{t('price')}</TableCell>
              <TableCell>{t('sum-price')}</TableCell>
              {isVatIncluded && (
                <TableCell>
                  <Typography
                    sx={{
                      whiteSpace: 'wrap',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      fontSize: '14px',
                    }}
                  >
                    {t('price-with-vat')}
                  </Typography>
                </TableCell>
              )}
              <TableCell>{t('comment')}</TableCell>
              <TableCell>{t('file')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {modifiedProcurementSubmissionItems.map((item, index) => {
              return (
                <TableRow key={item.id}>
                  <TableCell>{item.startRowNumber}</TableCell>
                  <StyledTableCell>
                    <Controller
                      name={`procurementRequirement.procurementRequirementItems.${index}.name`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} disabled fullWidth />
                      )}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Controller
                      name={`procurementRequirement.procurementRequirementItems.${index}.description`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} disabled fullWidth />
                      )}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Controller
                      name={`procurementRequirement.procurementRequirementItems.${index}.quantity`}
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} disabled fullWidth />
                      )}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Controller
                      name={`procurementRequirement.procurementRequirementItems.${index}.measurement`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          disabled
                          fullWidth
                          value={t(field.value)}
                        />
                      )}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Controller
                      name={`procurementRequirement.procurementRequirementItems.${index}.price`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          disabled={hasDocDeadlinePassed}
                          onChange={(e) => {
                            field.onChange(e)
                            setPriceValue(e.target.value)
                          }}
                        />
                      )}
                    />
                  </StyledTableCell>
                  <TableCell>
                    <Typography>{item.calculatedTotalPrice}</Typography>
                  </TableCell>
                  {isVatIncluded && (
                    <TableCell sx={{ fontSize: '16px' }}>
                      {item.calculatedVatPrice}
                    </TableCell>
                  )}
                  <StyledTableCell>
                    <Controller
                      name={`procurementRequirement.procurementRequirementItems.${index}.comment`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          disabled={hasDocDeadlinePassed}
                        />
                      )}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Controller
                      name={`procurementRequirement.procurementRequirementItems.${index}.attachedFileId`}
                      control={control}
                      render={({ field }) => (
                        <FileUpload
                          text={t('extra-document')}
                          handleFile={(file) => field.onChange(file.id)}
                          fileId={field.value}
                          accept="image/png, image/gif, image/jpeg"
                          previewImage
                          isDisabled
                        />
                      )}
                    />
                  </StyledTableCell>
                </TableRow>
              )
            })}
          </TableBody>
          <TableBody>
            <TableRow>
              <TableCell align="right" colSpan={tableColumnSpan}>
                {t('final-total-price')}
              </TableCell>
              <TableCell align="right">
                {procurementSubmission.totalPriceSum}
              </TableCell>
              <TableCell align="left">AZN</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right" colSpan={tableColumnSpan}>
                {t('calculated-total-vat')}
              </TableCell>
              <TableCell align="right">
                {isVatIncluded ? procurementSubmission.vatTotalPriceSum : 0}
              </TableCell>
              <TableCell align="left">AZN</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right" colSpan={tableColumnSpan}>
                {t('tender-submission-final-price')}
              </TableCell>
              <TableCell align="right">
                {_round(
                  Number(procurementSubmission.totalPriceSum) +
                    (isVatIncluded
                      ? Number(procurementSubmission.vatTotalPriceSum)
                      : 0),
                  2
                )}
              </TableCell>
              <TableCell align="left">AZN</TableCell>
            </TableRow>
          </TableBody>
        </StyledTable>
      </TableContainer>
    </Stack>
  )
}
